import { PdfDownaloadIcon } from 'assets/icons';
import NoPost from 'components/Blog/NoPost';
import {
  CommonSmallBadge,
  EventTagBadge,
  PDFDownloadBadge,
} from 'components/Common/CommonSmallBadge';
import { GlassLoader } from 'components/Common/GlassLoader';
import { useEventContext } from 'context/EventContext';
import {
  BB,
  BodyTextS,
  BodyTextSStrong,
  GRAY40,
  GRAY60,
  SubtitleM,
} from 'global-styles';
import { MainButton } from 'global-styles/MainButton';
import { useRouter } from 'next/router';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { useEventList } from 'react-query/eventHooks';
import styled from 'styled-components';
import { generateCssStyle } from 'styles/breakPoints';
import { statusItems } from './EventSearchArea';

const typeLabels = {
  en: {
    international: 'International Exhibition',
    domestic: 'Domestic Exhibition',
  },
  ko: {
    international: '해외전시',
    domestic: '국내전시',
  },
};

const EventListSection = () => {
  const { locale } = useRouter();
  const {
    data: eventListRes,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useEventList();
  const {
    state: { prevScrollTop },
    dispatch,
  } = useEventContext();

  const noEvents = useMemo(() => {
    if (isLoading) return false;
    const flatPosts = eventListRes?.pages
      .map(page => page?.events)
      .flat()
      .flat();
    return !flatPosts || flatPosts.length === 0;
  }, [eventListRes, isLoading]);

  useEffect(() => {
    if (prevScrollTop) window.scrollTo({ top: prevScrollTop });

    dispatch({ type: 'change', newState: { prevScrollTop: 0 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: 'change', newState: { currPageNum: 0 } });
  }, [locale, dispatch]);

  if (isLoading)
    return (
      <EventListSectionContainer>
        <GlassLoader loadingText="Loading events..." />
      </EventListSectionContainer>
    );

  return (
    <EventListSectionContainer>
      {noEvents ? (
        <NoPost
          text={{
            en: 'Matched result not found.',
            ko: '검색 결과에 해당하는 이벤트가 없습니다.',
          }}
        />
      ) : (
        <ul className="event-card-list">
          {eventListRes?.pages?.map(eventList => {
            return eventList?.events?.map(event => (
              <li className="m-c" key={event.eid}>
                <ConditionalHrefProvider
                  href={
                    event.status !== 'closed'
                      ? event.event_link || undefined
                      : undefined
                  }
                >
                  <div className="left-wrapper">
                    <img
                      draggable={false}
                      src={event.thumbnail_url || ''}
                      className="target-img"
                      alt={`${event.title || ''} image`}
                    />
                  </div>
                  <div className="right-wrapper">
                    {/* category */}
                    <BodyTextSStrong className="ev-type">
                      {typeLabels[locale][event.type] || '-'}
                    </BodyTextSStrong>

                    {/* title, progess badge */}
                    <div className="ev-t">
                      <SubtitleM className="ev-t-t">
                        {event.title || '-'}
                      </SubtitleM>
                      <CommonSmallBadge
                        colorType={
                          event.status === 'inprogress'
                            ? 'Fill'
                            : event.status === 'scheduled'
                            ? 'Outline'
                            : 'Disabled'
                        }
                      >
                        {
                          statusItems.find(item => item.value === event.status)
                            ?.label[locale]
                        }
                      </CommonSmallBadge>

                      <PDFDownloadBadge
                        style={{ marginLeft: '4px' }}
                        colorType={event.has_pdf ? 'Outline' : 'Disabled'}
                        onClickCapture={e => {
                          e.stopPropagation();
                          if (!event.has_pdf) return;
                          const $a = document.createElement('a');
                          console.log(event);
                          $a.href = event.pdf_url;
                          $a.target = '_blank';
                          $a.click();
                          $a.remove();
                        }}
                      >
                        <div
                          style={{ display: 'flex', alignContent: 'center' }}
                        >
                          <PdfDownaloadIcon />
                          <span style={{ marginLeft: '4px' }}>
                            {locale === 'ko' ? '제품 소개' : 'Product Info'}
                          </span>
                        </div>
                      </PDFDownloadBadge>
                    </div>

                    {/* location */}
                    <div className="loc-wrapper">
                      <BodyTextSStrong className="ev-loc" as="div">
                        {event.location || ''}
                      </BodyTextSStrong>

                      {/* booth location */}
                      <BodyTextSStrong className="ev-b-loc" as="div">
                        {event.booth_location || ''}
                      </BodyTextSStrong>
                    </div>

                    {/* date range */}
                    <BodyTextS className="ev-d-r">
                      <span>{event.start_time?.slice(0, 10) || '-'}</span>
                      <span> ~ </span>
                      <span>{event.end_time?.slice(0, 10) || '-'}</span>
                    </BodyTextS>

                    {/* description */}
                    {event.description && (
                      <BodyTextS className="ev-description-area" as="pre">
                        {event.description}
                      </BodyTextS>
                    )}

                    {/* event tags */}
                    {event.tags && (
                      <div className="tags-wrapper">
                        {event.tags?.split('#').map(tag => (
                          <EventTagBadge key={tag}>
                            <span>#</span>
                            <span>{tag}</span>
                          </EventTagBadge>
                        ))}
                      </div>
                    )}
                  </div>
                </ConditionalHrefProvider>
              </li>
            ));
          })}
        </ul>
      )}

      {!noEvents && (
        <MainButton
          className="p-b-btn"
          buttonType={
            isFetchingNextPage || !hasNextPage ? 'disabled' : 'default'
          }
          text={
            isFetchingNextPage
              ? locale === 'en'
                ? 'Loading...'
                : '로드중...'
              : hasNextPage
              ? locale === 'en'
                ? 'Load next'
                : '더 보기'
              : locale === 'en'
              ? 'You are on the last page.'
              : '마지막 페이지 입니다.'
          }
          injectInlineStyles={{ height: '48px', marginTop: '56px' }}
          injectCssStyles={[
            generateCssStyle({
              width: hasNextPage ? '156px' : 'fit-content',
              padding: hasNextPage ? '0' : '0 32px',
            }),
          ]}
          onClick={() => fetchNextPage()}
        />
      )}
    </EventListSectionContainer>
  );
};

export default EventListSection;

const EventListSectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .target-img {
    object-fit: contain;
    background: #000;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    position: absolute;
    transition: transform 0.2s ease-in;
  }
  .event-card-list {
    width: 100%;
  }
  .event-card {
    width: 100%;
    position: relative;
    padding: 28px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
  }
  .event-card:hover .target-img {
    transform: scale(1.05);
  }
  .m-c + .m-c {
    margin-top: 32px;
  }
  .left-wrapper {
    overflow: hidden;
    position: relative;
    width: 360px;
    height: 240px;
    flex-shrink: 0;
  }
  .right-wrapper {
    width: 100%;
    margin-left: 32px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
  }
  .ev-type {
    color: ${BB};
    margin-bottom: 0.4rem;
  }
  .ev-t {
    display: flex;
    align-items: center;
  }
  .ev-t-t {
    margin-right: 1rem;
    font-size: 20px;
  }
  .ev-loc {
    color: ${GRAY40};
  }
  .loc-wrapper {
    margin: 1.6rem 0;
  }
  .ev-b-loc {
    color: ${GRAY40};
  }
  .ev-d-r {
    color: ${GRAY60};
  }
  .tags-wrapper {
    display: flex;
    margin-top: 16px;
    flex-wrap: wrap;
    gap: 16px;
  }
  .ev-description-area {
    color: ${GRAY60};
    margin-top: 24px;
    white-space: pre-wrap;
  }
  @media screen and (max-width: 768px) {
    .event-card {
      flex-direction: column;
    }
    .left-wrapper {
      width: 100%;
      padding-top: 66.67%;
    }
    .right-wrapper {
      margin-top: 31px;
      margin-left: 0;
    }
  }
`;

type ConditionalHrefProviderProps = PropsWithChildren<{
  href?: string;
}>;

const ConditionalHrefProvider = ({
  href,
  children,
}: ConditionalHrefProviderProps) => {
  if (href)
    return (
      <ConditionalHrefAnchor
        className="event-card"
        target="_blank"
        href={href.includes('https://') ? href : `https://${href}`}
        style={{ display: 'flex' }}
      >
        {children}
      </ConditionalHrefAnchor>
    );

  return <div className="event-card">{children}</div>;
};

const ConditionalHrefAnchor = styled.a`
  color: #000;
  &:active {
    box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.2) !important;
  }
  &:hover {
    box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.3) !important;
  }
`;
